/**
 * useUploadWithProgress.ts
 *
 * Custom React hook for file uploads with real-time progress tracking.
 * Uses XMLHttpRequest to track upload progress events.
 *
 * Features:
 * - Real-time upload progress (0-100%)
 * - 120 second timeout
 * - Abort capability
 * - Error handling (timeout, network, offline, server errors)
 * - User-friendly error messages
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { usePageVisibility } from './usePageVisibility'

export interface UploadResult {
  success: boolean
  uploadId?: string
  mediaUrl?: string
  error?: string
}

export interface UploadOptions {
  headers?: Record<string, string>
  additionalFormData?: Record<string, string>
  timeoutMs?: number
}

export interface UseUploadWithProgressReturn {
  uploadFile: (
    file: File,
    url: string,
    onProgress: (percent: number) => void,
    options?: UploadOptions
  ) => Promise<UploadResult>
  abort: () => void
  isUploading: boolean
  isPaused: boolean
}

/**
 * Maps error types to user-friendly messages
 */
const mapErrorToMessage = (error: Error | string): string => {
  const errorMessage = typeof error === 'string' ? error : error.message

  // Check if offline
  if (!navigator.onLine) {
    return 'No internet connection. Check your network'
  }

  // Timeout error
  if (errorMessage.toLowerCase().includes('timeout')) {
    return 'Upload timed out. Try again'
  }

  // Network/connection errors
  if (
    errorMessage.toLowerCase().includes('network') ||
    errorMessage.toLowerCase().includes('connection')
  ) {
    return 'Connection lost. Try again'
  }

  // DNS errors
  if (errorMessage.toLowerCase().includes('dns')) {
    return "Can't reach server. Check your internet connection"
  }

  // SSL/Certificate errors
  if (
    errorMessage.toLowerCase().includes('certificate') ||
    errorMessage.toLowerCase().includes('ssl')
  ) {
    return 'Connection blocked. Check your network or contact IT if on corporate network'
  }

  // Abort
  if (errorMessage.toLowerCase().includes('abort')) {
    return 'Upload cancelled'
  }

  // Default
  return 'Something went wrong. Try again'
}

/**
 * Maps HTTP status codes to user-friendly messages
 */
const mapStatusToMessage = (status: number, responseText?: string): string => {
  if (status === 401) {
    return 'Session expired. Please try again'
  }

  if (status === 429) {
    // Try to parse Retry-After header from response
    return 'Too many uploads. Try again in a few minutes'
  }

  if (status >= 500) {
    return 'Server error. Try again in a minute'
  }

  if (status >= 400) {
    // Try to parse error message from response
    try {
      const parsed = JSON.parse(responseText || '{}')
      if (parsed.message) {
        return parsed.message
      }
    } catch {
      // Ignore parsing errors
    }
    return 'Upload failed. Try again'
  }

  return 'Something went wrong. Try again'
}

/**
 * Custom hook for file uploads with progress tracking
 */
export const useUploadWithProgress = (): UseUploadWithProgressReturn => {
  const [isUploading, setIsUploading] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const xhrRef = useRef<XMLHttpRequest | null>(null)
  const { isVisible } = usePageVisibility()

  /**
   * Track pause state when page visibility changes
   * When page is hidden during upload, mark as paused for UI feedback
   * Upload continues in background, but user should know it's backgrounded
   */
  useEffect(() => {
    if (!isVisible && isUploading) {
      setIsPaused(true)
    } else if (isVisible && isPaused) {
      setIsPaused(false)
    }
  }, [isVisible, isUploading, isPaused])

  /**
   * Upload file with progress tracking
   */
  const uploadFile = useCallback(
    (
      file: File,
      url: string,
      onProgress: (percent: number) => void,
      options?: UploadOptions
    ): Promise<UploadResult> => {
      return new Promise((resolve, reject) => {
        // Create FormData
        const formData = new FormData()
        formData.append('file', file)

        // Add additional form data if provided
        if (options?.additionalFormData) {
          Object.entries(options.additionalFormData).forEach(([key, value]) => {
            formData.append(key, value)
          })
        }

        // Create XMLHttpRequest
        const xhr = new XMLHttpRequest()
        xhrRef.current = xhr

        // Set timeout to 120 seconds by default (as per PRD Phase 3).
        // Larger media uploads can pass a size-aware timeout.
        xhr.timeout = options?.timeoutMs ?? 120000

        setIsUploading(true)

        // Track upload progress
        xhr.upload.addEventListener('progress', (event) => {
          if (event.lengthComputable) {
            const percent = (event.loaded / event.total) * 100
            onProgress(percent)
          }
        })

        // Handle successful upload
        xhr.addEventListener('load', () => {
          setIsUploading(false)
          xhrRef.current = null

          if (xhr.status >= 200 && xhr.status < 300) {
            try {
              const response = JSON.parse(xhr.responseText)
              resolve({
                success: true,
                uploadId: response.id || response.uploadId,
                mediaUrl: response.mediaUrl || response.url,
              })
            } catch (parseError) {
              resolve({
                success: false,
                error: 'Invalid response from server',
              })
            }
          } else {
            resolve({
              success: false,
              error: mapStatusToMessage(xhr.status, xhr.responseText),
            })
          }
        })

        // Handle network errors
        xhr.addEventListener('error', () => {
          setIsUploading(false)
          xhrRef.current = null

          const error = new Error('Network error')
          resolve({
            success: false,
            error: mapErrorToMessage(error),
          })
        })

        // Handle timeout
        xhr.addEventListener('timeout', () => {
          setIsUploading(false)
          xhrRef.current = null

          const error = new Error('Upload timeout')
          resolve({
            success: false,
            error: mapErrorToMessage(error),
          })
        })

        // Handle abort
        xhr.addEventListener('abort', () => {
          setIsUploading(false)
          xhrRef.current = null

          resolve({
            success: false,
            error: 'Upload cancelled',
          })
        })

        // Start upload
        xhr.open('POST', url)

        // Set custom headers if provided
        if (options?.headers) {
          Object.entries(options.headers).forEach(([key, value]) => {
            xhr.setRequestHeader(key, value)
          })
        }

        xhr.send(formData)
      })
    },
    []
  )

  /**
   * Abort current upload
   */
  const abort = useCallback(() => {
    if (xhrRef.current) {
      xhrRef.current.abort()
      xhrRef.current = null
      setIsUploading(false)
    }
  }, [])

  return {
    uploadFile,
    abort,
    isUploading,
    isPaused,
  }
}
